import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface INote {
  id: string;
  type: 'personal' | 'class' | 'feedback';
  training_id: string;
  /** True only when note.type === 'class' AND the current user was the instructor for this training. */
  is_instructor_class?: boolean;
  training?: {
    id: string;
    title: string;
    start_time: string;
    end_time: string;
    is_class: boolean;
    type?: string;
    modality?: string;
    instructor_id?: string;
  };
  full_notes?: string;
  feedback_notes?: string;
  personal_notes?: string;
  overall_rating?: number;
  techniques?: Array<{
    id: number;
    name: string;
    name_br?: string;
    category: string;
  }>;
  partners?: Array<{
    id: string;
    partner_user_id?: string;
    manual_partner_name?: string;
    partner_name?: string;
    partner_username?: string;
    partner_avatar_url?: string;
    partner_belt_rank?: string;
    is_manual: boolean;
  }>;
  created_at: string;
  updated_at: string;
}

export interface IMonthlyNotesTotal {
  month: number;
  year: number;
  notes_count: number;
}

export interface IUseNotesParams {
  month?: number;
  year?: number;
  noteType?: 'personal' | 'class' | 'feedback' | 'all';
  category?: string;
  technique?: string;
  studentId?: string;
}

export interface IUseNotesReturn {
  notes: INote[];
  monthlyTotals: IMonthlyNotesTotal[];
  isLoading: boolean;
  error: Error | null;
}

// Separate hook for monthly totals - completely independent, always fetches ALL months
// Uses the SAME consolidation logic as useNotes to ensure counts match displayed notes
export const useMonthlyNotesTotals = (params?: { noteType?: 'personal' | 'class' | 'feedback' | 'all'; category?: string }): { monthlyTotals: IMonthlyNotesTotal[]; isLoading: boolean; error: Error | null } => {
  const { user } = useAuth();
  
  // Use stable queryKey - only user.id, no filters
  // This ensures the query is always the same and uses cache
  const queryKey = ['user-notes-monthly-totals', user?.id];
  
  const queryResult = useQuery({
    queryKey,
    queryFn: async (): Promise<IMonthlyNotesTotal[]> => {
      if (!user?.id) {
        return [];
      }

      // Use the SAME query logic as useNotes, but without month/year filtering
      // This ensures the counts match exactly what is displayed
      
      // Fetch ALL training notes with trainings data (need start_time for date filtering)
      const { data: trainingNotes } = await supabase
        .from('training_notes')
        .select(`
          id,
          training_id,
          full_notes,
          created_at,
          updated_at,
          overall_rating,
          trainings (
            id,
            start_time,
            end_time,
            status,
            is_class
          )
        `)
        .eq('user_id', user.id);

      // Fetch ALL partner notes with trainings data
      const { data: partnerNotes } = await supabase
        .from('partner_notes')
        .select(`
          id,
          training_id,
          personal_notes,
          feedback_notes,
          personal_techniques_applied,
          partner_techniques_applied,
          created_at,
          updated_at,
          trainings (
            id,
            start_time,
            end_time,
            status,
            is_class
          )
        `)
        .eq('user_id', user.id);

      // Get all unique training IDs
      const allTrainingIds = [
        ...new Set([
          ...(trainingNotes || []).map(n => n.training_id),
          ...(partnerNotes || []).map(n => n.training_id)
        ].filter(Boolean))
      ];

      // Fetch training partners
      const partnersMap: Record<string, any[]> = {};
      if (allTrainingIds.length > 0) {
        const { data: trainingPartners } = await supabase
          .from('training_partners')
          .select('training_id, partner_user_id, partner_username, manual_partner_name, is_manual')
          .in('training_id', allTrainingIds);

        if (trainingPartners) {
          trainingPartners.forEach((tp: any) => {
            if (!partnersMap[tp.training_id]) {
              partnersMap[tp.training_id] = [];
            }
            partnersMap[tp.training_id].push({
              partner_user_id: tp.partner_user_id,
              partner_username: tp.partner_username,
              manual_partner_name: tp.manual_partner_name,
              is_manual: tp.is_manual
            });
          });
        }
      }

      // Fetch general training techniques (for class notes only)
      const techniquesMap: Record<string, Array<{ id: number; name: string; name_br?: string; category: string }>> = {};
      if (allTrainingIds.length > 0) {
        const { data: trainingTechniques } = await supabase
          .from('training_techniques')
          .select('training_id, technique_id, techniques (id, name, name_br, category_id, technique_categories (name, name_br))')
          .in('training_id', allTrainingIds);

        if (trainingTechniques) {
          trainingTechniques.forEach((tt: any) => {
            if (!tt.training_id || !tt.techniques) return;
            const tech = Array.isArray(tt.techniques) ? tt.techniques[0] : tt.techniques;
            const category = Array.isArray(tech.technique_categories) 
              ? tech.technique_categories[0] 
              : tech.technique_categories;
            if (!techniquesMap[tt.training_id]) {
              techniquesMap[tt.training_id] = [];
            }
            techniquesMap[tt.training_id].push({
              id: tech.id,
              name: tech.name,
              name_br: tech.name_br,
              category: category?.name || 'Other'
            });
          });
        }
      }

      // Fetch personal and feedback techniques (same logic as useNotes)
      const personalTechniquesMap: Record<string, Array<{ id: number; name: string; name_br?: string; category: string }>> = {};
      const feedbackTechniquesMap: Record<string, Array<{ id: number; name: string; name_br?: string; category: string }>> = {};
      
      if (partnerNotes && partnerNotes.length > 0) {
        // Collect all unique technique names/IDs
        const allTechniqueNames = new Set<string>();
        partnerNotes.forEach((note: any) => {
          if (note.personal_techniques_applied && Array.isArray(note.personal_techniques_applied)) {
            note.personal_techniques_applied.forEach((t: string) => allTechniqueNames.add(t));
          }
          if (note.partner_techniques_applied && Array.isArray(note.partner_techniques_applied)) {
            note.partner_techniques_applied.forEach((t: string) => allTechniqueNames.add(t));
          }
        });

        if (allTechniqueNames.size > 0) {
          const techniqueNames = Array.from(allTechniqueNames);
          const techniqueQueries: Promise<any>[] = [];
          
          // Try to parse as IDs first
          const techniqueIds = techniqueNames.filter(n => /^\d+$/.test(n));
          if (techniqueIds.length > 0) {
            techniqueQueries.push(
              Promise.resolve(
                supabase
                  .from('techniques')
                  .select(`
                    id,
                    name,
                    name_br,
                    category_id,
                    technique_categories (
                      name,
                      name_br
                    )
                  `)
                  .in('id', techniqueIds.map(Number))
              ).then((result) => result)
            );
          }
          
          // Then try as names
          const techniqueNameStrings = techniqueNames.filter(n => !/^\d+$/.test(n));
          if (techniqueNameStrings.length > 0) {
            techniqueQueries.push(
              Promise.resolve(
                supabase
                  .from('techniques')
                  .select(`
                    id,
                    name,
                    name_br,
                    category_id,
                    technique_categories (
                      name,
                      name_br
                    )
                  `)
                  .in('name', techniqueNameStrings)
              ).then((result) => result)
            );
          }

          const techniqueResults = await Promise.all(techniqueQueries);
          
          // Create a map of technique name/id to technique object
          const techniqueLookup: Record<string, any> = {};
          techniqueResults.forEach(({ data: techniques, error }) => {
            if (error) {
              console.error('Error fetching techniques:', error);
            }
            if (!error && techniques) {
              techniques.forEach((tech: any) => {
                const categoryName = Array.isArray(tech.technique_categories)
                  ? tech.technique_categories[0]?.name
                  : tech.technique_categories?.name;
                const techObj = {
                  id: tech.id,
                  name: tech.name,
                  name_br: tech.name_br,
                  category: categoryName || 'Other'
                };
                techniqueLookup[tech.id.toString()] = techObj;
                techniqueLookup[tech.name] = techObj;
                if (tech.name_br) {
                  techniqueLookup[tech.name_br] = techObj;
                }
              });
            }
          });

          // Populate personal and feedback techniques maps
          partnerNotes.forEach((note: any) => {
            if (!note.id) return;
            
            // Process personal_techniques_applied
            if (note.personal_techniques_applied && Array.isArray(note.personal_techniques_applied) && note.personal_techniques_applied.length > 0) {
              personalTechniquesMap[note.id] = [];
              note.personal_techniques_applied.forEach((techNameOrId: string) => {
                const tech = techniqueLookup[techNameOrId];
                if (tech && !personalTechniquesMap[note.id].some(t => t.id === tech.id)) {
                  personalTechniquesMap[note.id].push(tech);
                }
              });
            }

            // Process partner_techniques_applied
            if (note.partner_techniques_applied && Array.isArray(note.partner_techniques_applied) && note.partner_techniques_applied.length > 0) {
              feedbackTechniquesMap[note.id] = [];
              note.partner_techniques_applied.forEach((techNameOrId: string) => {
                const tech = techniqueLookup[techNameOrId];
                if (tech && !feedbackTechniquesMap[note.id].some(t => t.id === tech.id)) {
                  feedbackTechniquesMap[note.id].push(tech);
                }
              });
            }
          });
        }
      }

      // Transform training notes (same logic as useNotes)
      const transformedTrainingNotes: INote[] = (trainingNotes || []).map(note => {
        const training = Array.isArray(note.trainings) ? note.trainings[0] : note.trainings;
        const techniques = techniquesMap[note.training_id] || [];
        const hasNotes = note.full_notes != null && note.full_notes.trim().length > 0;
        const hasTechniques = techniques.length > 0;
        
        // Only include if has notes OR has techniques (same logic as useNotes)
        if (!hasNotes && !hasTechniques) {
          return null;
        }
        
        return {
          id: note.id,
          type: 'class' as const,
          training_id: note.training_id,
          training: training,
          full_notes: note.full_notes || '',
          overall_rating: note.overall_rating,
          techniques: techniques,
          partners: partnersMap[note.training_id] || [],
          created_at: note.created_at,
          updated_at: note.updated_at
        };
      }).filter((note) => note !== null) as INote[];

      // Transform partner notes (same consolidation logic as useNotes)
      const personalNotesByTrainingId: Record<string, { content: string; techniques: Array<{ id: number; name: string; name_br?: string; category: string }>; training: any; created_at: string; updated_at: string }> = {};
      const feedbackNotesByTrainingId: Record<string, { content: string; techniques: Array<{ id: number; name: string; name_br?: string; category: string }>; training: any; created_at: string; updated_at: string }> = {};
      
      (partnerNotes || []).forEach(note => {
        const training = Array.isArray(note.trainings) ? note.trainings[0] : note.trainings;
        if (!training || !note.training_id) return;
        
        const personalTechniques = personalTechniquesMap[note.id] || [];
        const feedbackTechniques = feedbackTechniquesMap[note.id] || [];
        
        // Process personal notes - consolidate by training_id
        const hasPersonalNotes = note.personal_notes != null && String(note.personal_notes).trim().length > 0;
        if (hasPersonalNotes || personalTechniques.length > 0) {
          if (!personalNotesByTrainingId[note.training_id]) {
            personalNotesByTrainingId[note.training_id] = {
              content: '',
              techniques: [],
              training: training,
              created_at: note.created_at,
              updated_at: note.updated_at
            };
          }
          
          if (hasPersonalNotes && !personalNotesByTrainingId[note.training_id].content) {
            personalNotesByTrainingId[note.training_id].content = note.personal_notes || '';
          }
          
          personalTechniques.forEach(tech => {
            if (!personalNotesByTrainingId[note.training_id].techniques.some(t => t.id === tech.id)) {
              personalNotesByTrainingId[note.training_id].techniques.push(tech);
            }
          });
          
          if (new Date(note.updated_at) > new Date(personalNotesByTrainingId[note.training_id].updated_at)) {
            personalNotesByTrainingId[note.training_id].updated_at = note.updated_at;
          }
        }
        
        // Process feedback notes - consolidate by training_id
        const hasFeedbackNotes = note.feedback_notes != null && String(note.feedback_notes).trim().length > 0;
        if (hasFeedbackNotes || feedbackTechniques.length > 0) {
          if (!feedbackNotesByTrainingId[note.training_id]) {
            feedbackNotesByTrainingId[note.training_id] = {
              content: '',
              techniques: [],
              training: training,
              created_at: note.created_at,
              updated_at: note.updated_at
            };
          }
          
          if (hasFeedbackNotes && !feedbackNotesByTrainingId[note.training_id].content) {
            feedbackNotesByTrainingId[note.training_id].content = note.feedback_notes || '';
          }
          
          feedbackTechniques.forEach(tech => {
            if (!feedbackNotesByTrainingId[note.training_id].techniques.some(t => t.id === tech.id)) {
              feedbackNotesByTrainingId[note.training_id].techniques.push(tech);
            }
          });
          
          if (new Date(note.updated_at) > new Date(feedbackNotesByTrainingId[note.training_id].updated_at)) {
            feedbackNotesByTrainingId[note.training_id].updated_at = note.updated_at;
          }
        }
      });
      
      // Create consolidated notes (same logic as useNotes)
      const transformedPartnerNotes: INote[] = [];
      
      Object.entries(personalNotesByTrainingId).forEach(([trainingId, data]) => {
        const hasContent = data.content.trim().length > 0;
        const hasTechniques = data.techniques.length > 0;
        
        if (hasContent || hasTechniques) {
          transformedPartnerNotes.push({
            id: `personal-${trainingId}`,
            type: 'personal',
            training_id: trainingId,
            training: data.training,
            personal_notes: data.content,
            techniques: data.techniques,
            partners: partnersMap[trainingId] || [],
            created_at: data.created_at,
            updated_at: data.updated_at
          });
        }
      });
      
      Object.entries(feedbackNotesByTrainingId).forEach(([trainingId, data]) => {
        const hasContent = data.content.trim().length > 0;
        const hasTechniques = data.techniques.length > 0;
        
        if (hasContent || hasTechniques) {
          transformedPartnerNotes.push({
            id: `feedback-${trainingId}`,
            type: 'feedback',
            training_id: trainingId,
            training: data.training,
            feedback_notes: data.content,
            techniques: data.techniques,
            partners: partnersMap[trainingId] || [],
            created_at: data.created_at,
            updated_at: data.updated_at
          });
        }
      });

      // Combine all notes (same logic as useNotes)
      const notesByKey: Record<string, INote> = {};
      
      transformedTrainingNotes.forEach(note => {
        notesByKey[`class-${note.training_id}`] = note;
      });
      
      transformedPartnerNotes.forEach(note => {
        const noteKey = `${note.type}-${note.training_id}`;
        notesByKey[noteKey] = note;
      });
      
      let allNotes = Object.values(notesByKey);

      // Count notes by month/year using training.start_time (same as useNotes filtering)
      const monthlyTotalsMap: Record<string, number> = {};
      
      allNotes.forEach(note => {
        // Use training.start_time, not note.created_at (same as useNotes)
        if (!note.training?.start_time) {
          return; // Skip notes without training data
        }
        
        try {
          const trainingDate = new Date(note.training.start_time);
          const month = trainingDate.getMonth() + 1;
          const year = trainingDate.getFullYear();
          const key = `${year}-${month}`;
          monthlyTotalsMap[key] = (monthlyTotalsMap[key] || 0) + 1;
        } catch (error) {
          console.warn('Error parsing training date for monthly totals:', note.training.start_time, error);
        }
      });

      const monthlyTotals: IMonthlyNotesTotal[] = Object.entries(monthlyTotalsMap).map(([key, count]) => {
        const [year, month] = key.split('-').map(Number);
        return { month, year, notes_count: count };
      });

      return monthlyTotals;
    },
    enabled: !!user?.id,
    staleTime: 10 * 60 * 1000, // 10 minutes - keep data fresh longer
    gcTime: 30 * 60 * 1000, // 30 minutes - keep in cache longer
    refetchOnMount: false, // Don't refetch on mount if data is fresh
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });

  return {
    monthlyTotals: queryResult.data || [],
    isLoading: queryResult.isLoading,
    error: queryResult.error as Error | null,
  };
};

export const useNotes = (params?: IUseNotesParams): IUseNotesReturn => {
  const { user } = useAuth();
  
  const queryKey = [
    'user-notes',
    user?.id,
    params?.month ?? null,
    params?.year ?? null,
    params?.noteType ?? null,
    params?.category ?? null,
    params?.technique ?? null,
    params?.studentId ?? null,
  ];
  
  const queryResult = useQuery({
    queryKey,
    queryFn: async (): Promise<{ notes: INote[]; monthlyTotals: IMonthlyNotesTotal[] }> => {
      if (!user?.id) {
        return { notes: [], monthlyTotals: [] };
      }

      try {
        // Fetch training notes (personal notes and class notes)
        const { data: trainingNotes, error: trainingNotesError } = await supabase
          .from('training_notes')
          .select(`
            id,
            training_id,
            full_notes,
            overall_rating,
            created_at,
            updated_at,
            trainings!inner (
              id,
              title,
              start_time,
              end_time,
              is_class,
              type,
              modality,
              instructor_id
            )
          `)
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (trainingNotesError) {
          console.error('Error fetching training notes:', trainingNotesError);
          throw trainingNotesError; // Throw error so React Query can handle it
        }


      // Fetch partner notes (feedback)
      const { data: partnerNotes, error: partnerNotesError } = await supabase
        .from('partner_notes')
        .select(`
          id,
          training_id,
          feedback_notes,
          personal_notes,
          personal_techniques_applied,
          partner_techniques_applied,
          roll_categories,
          created_at,
          updated_at,
          trainings!inner (
            id,
            title,
            start_time,
            end_time,
            is_class,
            type,
            modality,
            instructor_id
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

        if (partnerNotesError) {
          console.error('Error fetching partner notes:', partnerNotesError);
          throw partnerNotesError; // Throw error so React Query can handle it
        }


      // Fetch techniques for all notes
      const allTrainingIds = [
        ...(trainingNotes || []).map(n => n.training_id),
        ...(partnerNotes || []).map(n => n.training_id)
      ];

      // Fetch training partners
      let partnersMap: Record<string, any[]> = {};
      if (allTrainingIds.length > 0) {
        const { data: trainingPartners, error: partnersError } = await supabase
          .from('training_partners')
          .select(`
            id,
            training_id,
            partner_user_id,
            manual_partner_name,
            profiles:partner_user_id (
              id,
              full_name,
              username,
              profile_picture_url,
              belt_rank
            )
          `)
          .in('training_id', allTrainingIds);

        if (!partnersError && trainingPartners) {
          trainingPartners.forEach((tp: any) => {
            if (!tp.training_id) return;
            if (!partnersMap[tp.training_id]) {
              partnersMap[tp.training_id] = [];
            }
            const partner = {
              id: tp.id,
              partner_user_id: tp.partner_user_id,
              manual_partner_name: tp.manual_partner_name,
              partner_name: tp.partner_user_id && tp.profiles
                ? (Array.isArray(tp.profiles) ? tp.profiles[0]?.full_name : tp.profiles.full_name)
                : tp.manual_partner_name,
              partner_username: tp.partner_user_id && tp.profiles
                ? (Array.isArray(tp.profiles) ? tp.profiles[0]?.username : tp.profiles.username)
                : undefined,
              partner_avatar_url: tp.partner_user_id && tp.profiles
                ? (Array.isArray(tp.profiles) ? tp.profiles[0]?.profile_picture_url : tp.profiles.profile_picture_url)
                : undefined,
              partner_belt_rank: tp.partner_user_id && tp.profiles
                ? (Array.isArray(tp.profiles) ? tp.profiles[0]?.belt_rank : tp.profiles.belt_rank)
                : undefined,
              is_manual: !!tp.manual_partner_name
            };
            partnersMap[tp.training_id].push(partner);
          });
        }
      }

      let techniquesMap: Record<string, any[]> = {};
      if (allTrainingIds.length > 0) {
        const { data: trainingTechniques, error: techniquesError } = await supabase
          .from('training_techniques')
          .select(`
            training_id,
            techniques (
              id,
              name,
              name_br,
              category_id,
              technique_categories (
                name,
                name_br
              )
            )
          `)
          .in('training_id', allTrainingIds);

        if (!techniquesError && trainingTechniques) {
          trainingTechniques.forEach((tt: any) => {
            if (!tt.training_id) return;
            if (!techniquesMap[tt.training_id]) {
              techniquesMap[tt.training_id] = [];
            }
            if (tt.techniques) {
              const categoryName = Array.isArray(tt.techniques.technique_categories)
                ? tt.techniques.technique_categories[0]?.name
                : tt.techniques.technique_categories?.name;
              techniquesMap[tt.training_id].push({
                id: tt.techniques.id,
                name: tt.techniques.name,
                name_br: tt.techniques.name_br,
                category: categoryName || 'Other'
              });
            }
          });
        }
      }

      // Create separate maps for personal and feedback techniques
      // These techniques belong specifically to each note type
      const personalTechniquesMap: Record<string, Array<{ id: number; name: string; name_br?: string; category: string }>> = {};
      const feedbackTechniquesMap: Record<string, Array<{ id: number; name: string; name_br?: string; category: string }>> = {};
      
      // Also fetch techniques from partner_notes arrays (personal_techniques_applied and partner_techniques_applied)
      // These are stored as arrays of technique names or IDs
      if (partnerNotes && partnerNotes.length > 0) {
        // Collect all unique technique names/IDs from partner notes
        const allPartnerTechniqueNames: string[] = [];
        partnerNotes.forEach((note: any) => {
          if (note.personal_techniques_applied && Array.isArray(note.personal_techniques_applied)) {
            allPartnerTechniqueNames.push(...note.personal_techniques_applied);
          }
          if (note.partner_techniques_applied && Array.isArray(note.partner_techniques_applied)) {
            allPartnerTechniqueNames.push(...note.partner_techniques_applied);
          }
        });

        // Remove duplicates
        const uniqueTechniqueNames = [...new Set(allPartnerTechniqueNames)];

        if (uniqueTechniqueNames.length > 0) {
          // Separate IDs (numeric strings) from names
          const techniqueIds = uniqueTechniqueNames.filter(name => /^\d+$/.test(name)).map(id => parseInt(id, 10));
          const techniqueNames = uniqueTechniqueNames.filter(name => !/^\d+$/.test(name));

          // Fetch techniques by ID and by name
          const techniqueQueries: Promise<any>[] = [];
          
          if (techniqueIds.length > 0) {
            techniqueQueries.push(
              Promise.resolve(
                supabase
                  .from('techniques')
                  .select(`
                    id,
                    name,
                    name_br,
                    category_id,
                    technique_categories (
                      name,
                      name_br
                    )
                  `)
                  .in('id', techniqueIds)
              ).then((result) => result)
            );
          }

          if (techniqueNames.length > 0) {
            techniqueQueries.push(
              Promise.resolve(
                supabase
                  .from('techniques')
                  .select(`
                    id,
                    name,
                    name_br,
                    category_id,
                    technique_categories (
                      name,
                      name_br
                    )
                  `)
                  .in('name', techniqueNames)
              ).then((result) => result)
            );
          }

          const techniqueResults = await Promise.all(techniqueQueries);
          
          // Create a map of technique name/id to technique object
          const techniqueLookup: Record<string, any> = {};
          techniqueResults.forEach(({ data: techniques, error }) => {
            if (error) {
              console.error('Error fetching techniques:', error);
            }
            if (!error && techniques) {
              techniques.forEach((tech: any) => {
                const categoryName = Array.isArray(tech.technique_categories)
                  ? tech.technique_categories[0]?.name
                  : tech.technique_categories?.name;
                const techObj = {
                  id: tech.id,
                  name: tech.name,
                  name_br: tech.name_br,
                  category: categoryName || 'Other'
                };
                // Map by both ID and name for lookup
                techniqueLookup[tech.id.toString()] = techObj;
                techniqueLookup[tech.name] = techObj;
                if (tech.name_br) {
                  techniqueLookup[tech.name_br] = techObj;
                }
              });
            }
          });

          // Populate personal and feedback techniques maps
          partnerNotes.forEach((note: any) => {
            if (!note.id) return;
            
            // Process personal_techniques_applied - belongs to personal notes
            if (note.personal_techniques_applied && Array.isArray(note.personal_techniques_applied) && note.personal_techniques_applied.length > 0) {
              personalTechniquesMap[note.id] = [];
              note.personal_techniques_applied.forEach((techNameOrId: string) => {
                const tech = techniqueLookup[techNameOrId];
                if (tech && !personalTechniquesMap[note.id].some(t => t.id === tech.id)) {
                  personalTechniquesMap[note.id].push(tech);
                }
              });
            }

            // Process partner_techniques_applied - belongs to feedback notes
            if (note.partner_techniques_applied && Array.isArray(note.partner_techniques_applied) && note.partner_techniques_applied.length > 0) {
              feedbackTechniquesMap[note.id] = [];
              note.partner_techniques_applied.forEach((techNameOrId: string) => {
                const tech = techniqueLookup[techNameOrId];
                if (tech && !feedbackTechniquesMap[note.id].some(t => t.id === tech.id)) {
                  feedbackTechniquesMap[note.id].push(tech);
                }
              });
            }
          });
        }
      }

      // Transform training notes
      // Full Training Notes always appear as "Class Notes"
      const transformedTrainingNotes: INote[] = (trainingNotes || []).map(note => {
        // Handle both array and object formats for trainings
        const training = Array.isArray(note.trainings) ? note.trainings[0] : note.trainings;
        const techniques = techniquesMap[note.training_id] || [];
        // Check if has notes (handle null, undefined, and empty strings)
        const hasNotes = note.full_notes != null && note.full_notes.trim().length > 0;
        const hasTechniques = techniques.length > 0;
        
        // Only include if has notes OR has techniques (prioritize techniques)
        if (!hasNotes && !hasTechniques) {
          return null;
        }
        
        return {
          id: note.id,
          type: 'class' as const, // Always "Class Notes" for full_notes
          training_id: note.training_id,
          is_instructor_class: training?.instructor_id === user.id,
          training: training,
          full_notes: note.full_notes || '',
          overall_rating: note.overall_rating,
          techniques: techniques,
          partners: partnersMap[note.training_id] || [],
          created_at: note.created_at,
          updated_at: note.updated_at
        };
      }).filter((note) => note !== null) as INote[];

      // Transform partner notes
      // Consolidate notes by training_id and type (personal or feedback)
      // Each training_id should have at most one personal note and one feedback note
      // Aggregate all content and techniques from all partner_notes entries for the same training_id
      const personalNotesByTrainingId: Record<string, { content: string; techniques: Array<{ id: number; name: string; name_br?: string; category: string }>; training: any; created_at: string; updated_at: string }> = {};
      const feedbackNotesByTrainingId: Record<string, { content: string; techniques: Array<{ id: number; name: string; name_br?: string; category: string }>; training: any; created_at: string; updated_at: string }> = {};
      
      (partnerNotes || []).forEach(note => {
        // Handle both array and object formats for trainings
        const training = Array.isArray(note.trainings) ? note.trainings[0] : note.trainings;
        if (!training || !note.training_id) return;
        
        // Get techniques specific to this note entry
        const personalTechniques = personalTechniquesMap[note.id] || [];
        const feedbackTechniques = feedbackTechniquesMap[note.id] || [];
        // Also get general training techniques (from training_techniques table)
        const generalTechniques = techniquesMap[note.training_id] || [];
        
        // Process personal notes - consolidate by training_id
        const hasPersonalNotes = note.personal_notes != null && String(note.personal_notes).trim().length > 0;
        if (hasPersonalNotes || personalTechniques.length > 0) {
          if (!personalNotesByTrainingId[note.training_id]) {
            personalNotesByTrainingId[note.training_id] = {
              content: '',
              techniques: [],
              training: training,
              created_at: note.created_at,
              updated_at: note.updated_at
            };
          }
          
          // Aggregate content (use first non-empty content found)
          if (hasPersonalNotes && !personalNotesByTrainingId[note.training_id].content) {
            personalNotesByTrainingId[note.training_id].content = note.personal_notes || '';
          }
          
          // Aggregate techniques (avoid duplicates)
          personalTechniques.forEach(tech => {
            if (!personalNotesByTrainingId[note.training_id].techniques.some(t => t.id === tech.id)) {
              personalNotesByTrainingId[note.training_id].techniques.push(tech);
            }
          });
          
          // Update timestamps to most recent
          if (new Date(note.updated_at) > new Date(personalNotesByTrainingId[note.training_id].updated_at)) {
            personalNotesByTrainingId[note.training_id].updated_at = note.updated_at;
          }
        }
        
        // Process feedback notes - consolidate by training_id
        const hasFeedbackNotes = note.feedback_notes != null && String(note.feedback_notes).trim().length > 0;
        if (hasFeedbackNotes || feedbackTechniques.length > 0) {
          if (!feedbackNotesByTrainingId[note.training_id]) {
            feedbackNotesByTrainingId[note.training_id] = {
              content: '',
              techniques: [],
              training: training,
              created_at: note.created_at,
              updated_at: note.updated_at
            };
          }
          
          // Aggregate content (use first non-empty content found)
          if (hasFeedbackNotes && !feedbackNotesByTrainingId[note.training_id].content) {
            feedbackNotesByTrainingId[note.training_id].content = note.feedback_notes || '';
          }
          
          // Aggregate techniques (avoid duplicates)
          feedbackTechniques.forEach(tech => {
            if (!feedbackNotesByTrainingId[note.training_id].techniques.some(t => t.id === tech.id)) {
              feedbackNotesByTrainingId[note.training_id].techniques.push(tech);
            }
          });
          
          // Update timestamps to most recent
          if (new Date(note.updated_at) > new Date(feedbackNotesByTrainingId[note.training_id].updated_at)) {
            feedbackNotesByTrainingId[note.training_id].updated_at = note.updated_at;
          }
        }
      });
      
      // Now create consolidated notes from the aggregated data
      const transformedPartnerNotes: INote[] = [];
      
      // Create personal notes (one per training_id)
      // Personal notes should ONLY include personal_techniques_applied, NOT general training techniques
      Object.entries(personalNotesByTrainingId).forEach(([trainingId, data]) => {
        const hasContent = data.content.trim().length > 0;
        const hasTechniques = data.techniques.length > 0;
        
        // Render if has content OR has personal-specific techniques
        // Do NOT include general training techniques here - those belong to class notes only
        if (hasContent || hasTechniques) {
          transformedPartnerNotes.push({
            id: `personal-${trainingId}`,
            type: 'personal',
            training_id: trainingId,
            training: data.training,
            personal_notes: data.content,
            techniques: data.techniques, // Only personal-specific techniques
            partners: partnersMap[trainingId] || [],
            created_at: data.created_at,
            updated_at: data.updated_at
          });
        }
      });
      
      // Create feedback notes (one per training_id)
      // Feedback notes should ONLY include partner_techniques_applied, NOT general training techniques
      Object.entries(feedbackNotesByTrainingId).forEach(([trainingId, data]) => {
        const hasContent = data.content.trim().length > 0;
        const hasTechniques = data.techniques.length > 0;
        
        // Render if has content OR has feedback-specific techniques
        // Do NOT include general training techniques here - those belong to class notes only
        if (hasContent || hasTechniques) {
          // Use a consistent ID format to avoid duplicates
          const noteId = `feedback-${trainingId}`;
          
          // Check if we already added a note with this ID (shouldn't happen, but safety check)
          if (!transformedPartnerNotes.some(n => n.id === noteId)) {
            transformedPartnerNotes.push({
              id: noteId,
              type: 'feedback',
              training_id: trainingId,
              training: data.training,
              feedback_notes: data.content,
              techniques: data.techniques, // Only feedback-specific techniques
              partners: partnersMap[trainingId] || [],
              created_at: data.created_at,
              updated_at: data.updated_at
            });
          }
        }
      });

      // Combine all notes - don't consolidate different types
      // All note types (class, personal, feedback) should appear independently
      // Each type uses a unique key to ensure they all appear separately
      const notesByKey: Record<string, INote> = {};
      
      // Add training notes (class type)
      transformedTrainingNotes.forEach(note => {
        // Use unique key per type and training_id
        notesByKey[`class-${note.training_id}`] = note;
      });
      
      // Add partner notes (personal and feedback types) - always add, regardless of class notes
      transformedPartnerNotes.forEach(note => {
        // Use unique key per type and training_id to keep them separate
        // This ensures personal and feedback notes appear even if there's a class note
        const noteKey = `${note.type}-${note.training_id}`;
        // Safety check: if a note with this key already exists, log a warning but don't overwrite
        // (this shouldn't happen with proper consolidation, but helps debug)
        if (notesByKey[noteKey]) {
          console.warn(`Duplicate note key detected: ${noteKey}. Existing note ID: ${notesByKey[noteKey].id}, New note ID: ${note.id}`);
        }
        notesByKey[noteKey] = note;
      });
      
      // Convert to array and deduplicate techniques in each note
      let allNotes = Object.values(notesByKey).map(note => {
        if (note.techniques && note.techniques.length > 0) {
          const uniqueTechniques = note.techniques.filter((tech, index, self) => 
            index === self.findIndex(t => t.id === tech.id)
          );
          return { ...note, techniques: uniqueTechniques };
        }
        return note;
      });

      // Apply filters
      if (params?.noteType && params.noteType !== 'all') {
        allNotes = allNotes.filter(note => note.type === params.noteType);
      }

      if (params?.category) {
        allNotes = allNotes.filter(note => 
          note.techniques?.some(t => t.category === params.category)
        );
      }

      if (params?.technique) {
        allNotes = allNotes.filter(note =>
          note.techniques?.some(t => t.name === params.technique || t.name_br === params.technique)
        );
      }

      if (params?.studentId) {
        allNotes = allNotes.filter(note =>
          note.partners?.some(p => p.partner_user_id === params.studentId)
        );
      }

      // Filter by month/year if provided
      // Use training.start_time, not note.created_at, because notes are created after trainings
      if (params?.month && params?.year) {
        const beforeFilterCount = allNotes.length;
        allNotes = allNotes.filter(note => {
          // If training data is not available, skip this note (shouldn't happen, but safety check)
          if (!note.training?.start_time) {
            return false;
          }
          try {
            const trainingDate = new Date(note.training.start_time);
            const noteMonth = trainingDate.getMonth() + 1;
            const noteYear = trainingDate.getFullYear();
            const matches = noteMonth === params.month && noteYear === params.year;
            return matches;
          } catch (error) {
            console.warn('Error parsing training date:', note.training.start_time, error);
            return false;
          }
        });
      }

      // Calculate monthly totals from ALL notes (before filtering by month/year)
      // This ensures monthlyTotals always contains data for all months, regardless of params
      const monthlyTotalsMap: Record<string, number> = {};
      [...transformedTrainingNotes, ...transformedPartnerNotes].forEach(note => {
        const noteDate = new Date(note.created_at);
        const key = `${noteDate.getFullYear()}-${noteDate.getMonth() + 1}`;
        monthlyTotalsMap[key] = (monthlyTotalsMap[key] || 0) + 1;
      });

      const monthlyTotals: IMonthlyNotesTotal[] = Object.entries(monthlyTotalsMap).map(([key, count]) => {
        const [year, month] = key.split('-').map(Number);
        return { month, year, notes_count: count };
      });

        return { notes: allNotes, monthlyTotals: [] }; // Don't calculate monthlyTotals here
      } catch (error) {
        console.error('Error in useNotes queryFn:', error);
        throw error; // Re-throw so React Query can handle it
      }
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  return {
    notes: queryResult.data?.notes || [],
    monthlyTotals: [], // Use useMonthlyNotesTotals hook instead
    isLoading: queryResult.isLoading,
    error: queryResult.error as Error | null,
  };
};

